import React, { useState, useEffect, useCallback, useRef } from 'react';
import APIService from '../services/api';
import { ancre } from '../tutoriel/ancres';
import { confirmErpia } from './ConfirmDialog';
import { confirmationLever } from '../services/nePasContacter';
import { listerListeNoire, ajouterListeNoire, leverListeNoire, historiqueListeNoire } from '../services/listeNoire';

/**
 * LISTE NOIRE — les personnes qu'on ne contacte plus, en UN endroit, d'où
 * qu'elles y soient entrées : depuis une fiche, depuis une campagne de
 * sourcing, à leur demande par message, ou par le copilote. Demande du
 * 22.09 : « une blacklist partagée… blacklister quelqu'un du Sourceur… un
 * module blacklist ».
 *
 * TOUT VIENT DU SERVEUR : la marque vit sur la fiche, la barrière est chez
 * lui (InMail, appel, invitation, mode auto du moteur), l'origine et
 * l'auteur sont dans sa timeline. L'écran lit, pose, lève — et rattache : un
 * refus reçu par message SANS fiche apparaît quand même ici (`sans_fiche`),
 * avec le geste « Créer sa fiche » qui le protège partout.
 *
 * PLAN DE DESIGN (écran nouveau) — l'unité n'est pas « une personne », c'est
 * UN NON QU'ON DOIT RESPECTER. Ce que le prochain recruteur doit lire avant
 * de vouloir passer outre, c'est le MOTIF : il est donc la ligne de lecture
 * de chaque entrée, en corps de texte, pas un chip. Le nom est fort mais
 * court ; les identifiants (email, téléphone, page) sont en mono, petits :
 * ce sont les clés par lesquelles la barrière reconnaît la personne, pas le
 * sujet. À gauche, une colonne de dates en mono, alignée : « depuis quand »
 * se lit sans soustraction. Le seul rouge de l'écran est le filet inset des
 * lignes SANS FICHE — celles où la barrière ne tient que par une clé de
 * conversation, et qu'un clic rend solides : l'œil trouve ce qui demande un
 * geste avant de lire. Tout le reste est monochrome, bordures 1 px, aucune
 * ombre, chips d'origine calmes (structure = information : d'où vient le
 * non). Ce qu'on a écarté : un tableau Nom/Motif/Date/Auteur (il range, il
 * ne dit pas quoi lire), un rouge sur chaque ligne (tout rouge = rien de
 * rouge).
 */
const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });

const fmtDate = (v) => { if (!v) return ''; const d = new Date(v); return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: '2-digit' }); };
const ICONE_ORIGINE = { fiche: 'person-vcard', sourceur: 'linkedin', conversation: 'chat-left-text', agent: 'chat-square-dots', liste: 'slash-circle' };
const nomDe = (c) => `${c?.prenom || ''} ${c?.nom || ''}`.trim() || c?.name || '';
const idDe = (c) => c?.canonical_candidate_id ?? c?.candidate_id ?? c?.id;

export default function ListeNoire({ showNotification, onOuvrirCandidat }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [q, setQ] = useState('');
  const [origine, setOrigine] = useState('');
  const [busy, setBusy] = useState(null);
  const [histo, setHisto] = useState({});        // candidate_id -> [{...}]
  const [ajout, setAjout] = useState(null);       // { recherche, resultats, choisi, motif }
  const rechercheRef = useRef(0);

  const charger = useCallback(async () => {
    setErreur(null);
    const r = await listerListeNoire({ q, origine });
    if (!r.ok) { setErreur(r.absente ? 'arrive' : r.error); setData(null); return; }
    setData(r);
  }, [q, origine]);
  useEffect(() => { const t = setTimeout(charger, q ? 250 : 0); return () => clearTimeout(t); }, [charger, q]);

  const lever = async (e) => {
    const ok = await confirmErpia(confirmationLever(e.nom, e.motif));
    if (!ok) return;
    setBusy(e.candidate_id);
    const r = await leverListeNoire(e.candidate_id);
    showNotification?.(r.ok ? r.message : r.error, r.ok ? 'success' : 'error');
    setBusy(null);
    if (r.ok) charger();
  };
  const creerFiche = async (e) => {
    setBusy(`conv-${e.conversation_id}`);
    const r = await ajouterListeNoire({ conversation_id: e.conversation_id, motif: e.motif || 'à sa demande — réponse par message' });
    showNotification?.(r.ok ? r.message : r.error, r.ok ? 'success' : 'error');
    setBusy(null);
    if (r.ok) charger();
  };
  const voirHistorique = async (e) => {
    if (histo[e.candidate_id]) { setHisto(h => { const n = { ...h }; delete n[e.candidate_id]; return n; }); return; }
    const r = await historiqueListeNoire(e.candidate_id);
    if (!r.ok) { showNotification?.(r.error, 'error'); return; }
    setHisto(h => ({ ...h, [e.candidate_id]: r.data }));
  };

  // « Ajouter » : on cherche une fiche par son nom, on dit pourquoi.
  const chercherFiche = async (texte) => {
    const n = ++rechercheRef.current;
    setAjout(a => ({ ...(a || {}), recherche: texte, choisi: null }));
    if (!texte || texte.trim().length < 2) { setAjout(a => ({ ...(a || {}), resultats: [] })); return; }
    try {
      const r = await fetch(`${BASE}/candidats/vue?page=1&limit=8&search=${encodeURIComponent(texte.trim())}`, { headers: authH() });
      const j = await r.json().catch(() => ({}));
      if (n !== rechercheRef.current) return;
      const rows = (Array.isArray(j.candidats) && j.candidats) || (Array.isArray(j.candidates) && j.candidates) || (Array.isArray(j.data) && j.data) || (Array.isArray(j.data?.candidats) && j.data.candidats) || [];
      setAjout(a => ({ ...(a || {}), resultats: rows }));
    } catch { setAjout(a => ({ ...(a || {}), resultats: [] })); }
  };
  const ajouter = async (ev) => {
    ev.preventDefault();
    if (!ajout?.choisi) { showNotification?.('Choisissez une fiche dans la liste.', 'info'); return; }
    setBusy('ajout');
    const r = await ajouterListeNoire({ candidate_id: idDe(ajout.choisi), motif: ajout.motif || '', origine: 'liste' });
    showNotification?.(r.ok ? r.message : r.error, r.ok ? 'success' : 'error');
    setBusy(null);
    if (r.ok) { setAjout(null); charger(); }
  };

  const entrees = data?.entrees || [];
  const c = data?.compteurs || {};
  const origines = data?.origines || [];
  const titre = { fontFamily: "'Archivo',system-ui,sans-serif", fontWeight: 800, letterSpacing: '-0.02em' };
  const mono = { fontFamily: "'IBM Plex Mono',monospace" };
  const chip = (actif) => ({ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
    padding: '3px 9px', borderRadius: '6px', border: `1px solid ${actif ? 'var(--accent)' : 'var(--hair)'}`, background: actif ? 'var(--accent)' : 'transparent', color: actif ? 'var(--surface)' : 'var(--text-2)', cursor: 'pointer', whiteSpace: 'nowrap' });
  const chipOrigine = { display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '2px 8px', borderRadius: '6px', border: '1px solid var(--hair)', color: 'var(--text-2)', whiteSpace: 'nowrap' };

  return (
    <div style={{ padding: '22px 26px', maxWidth: '1060px' }} {...ancre('liste-noire')}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '14px', flexWrap: 'wrap', marginBottom: '4px' }}>
        <h1 style={{ ...titre, fontSize: '26px', margin: 0, color: 'var(--text-strong)' }}>Liste noire</h1>
        {data && (
          <span style={{ ...mono, fontSize: '12px', color: 'var(--text-2)' }}>
            {c.total || 0} personne{(c.total || 0) > 1 ? 's' : ''}
            {c.sans_fiche ? <span style={{ color: 'var(--danger-fg)', fontWeight: 600 }}> · {c.sans_fiche} sans fiche</span> : null}
          </span>
        )}
        <span style={{ flex: 1 }} />
        <button type="button" className="erp-btn erp-btn--secondary" onClick={() => setAjout(a => (a ? null : { recherche: '', resultats: [], choisi: null, motif: '' }))}>
          <BI icon={ajout ? 'x' : 'slash-circle'} /> {ajout ? 'Annuler' : 'Ajouter une personne'}
        </button>
      </div>
      <p style={{ margin: '0 0 16px', fontSize: '13px', color: 'var(--text-2)', maxWidth: '66ch', lineHeight: 1.55 }}>
        Les personnes qu'on ne contacte plus, d'où que vienne la décision : une fiche, une campagne de sourcing, une réponse « ne me sollicitez plus », le copilote.
        Pour chacune, le motif — c'est ce qu'on lit avant de vouloir passer outre — depuis quand, et qui l'a décidé. Le serveur refuse tout envoi vers elles, le moteur de sourcing compris.
      </p>

      {ajout && (
        <form onSubmit={ajouter} className="erp-card" style={{ padding: '14px 16px', marginBottom: '16px', display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) minmax(220px, 1.4fr) auto', gap: '10px', alignItems: 'start', background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <div style={{ position: 'relative' }}>
            <input className="erp-field" autoFocus value={ajout.recherche} placeholder="Nom de la fiche…" onChange={ev => chercherFiche(ev.target.value)} aria-label="Chercher une fiche candidat" />
            {ajout.choisi
              ? <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--text-2)' }}><BI icon="check2" /> {nomDe(ajout.choisi)}{ajout.choisi.email ? <span style={{ ...mono, color: 'var(--text-3)' }}> · {ajout.choisi.email}</span> : null}</div>
              : (ajout.resultats || []).length > 0 && (
                <div style={{ marginTop: '6px', border: '1px solid var(--hair)', borderRadius: '8px', overflow: 'hidden' }}>
                  {ajout.resultats.slice(0, 8).map(r => (
                    <button key={idDe(r)} type="button" onClick={() => setAjout(a => ({ ...a, choisi: r, recherche: nomDe(r) }))}
                      style={{ display: 'flex', gap: '8px', alignItems: 'baseline', width: '100%', textAlign: 'left', padding: '6px 9px', border: 'none', borderBottom: '1px solid var(--hair)', background: 'transparent', color: 'var(--text-strong)', fontSize: '12.5px', cursor: 'pointer' }}>
                      <span style={{ fontWeight: 600 }}>{nomDe(r) || `fiche ${idDe(r)}`}</span>
                      {r.titre && <span style={{ color: 'var(--text-3)', fontSize: '11.5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.titre}</span>}
                      {r.ne_pas_contacter === true && <span style={{ ...chipOrigine, color: 'var(--danger-fg)', borderColor: 'var(--danger-fg)', marginLeft: 'auto' }}>déjà</span>}
                    </button>
                  ))}
                </div>
              )}
          </div>
          <input className="erp-field" value={ajout.motif} placeholder="Motif — demande de la personne, consigne client, litige… (obligatoire)" onChange={ev => setAjout(a => ({ ...a, motif: ev.target.value }))} aria-label="Motif" />
          <button type="submit" className="erp-btn erp-btn--danger" disabled={busy === 'ajout' || !ajout.choisi || !ajout.motif.trim()}>
            <BI icon={busy === 'ajout' ? 'arrow-repeat' : 'slash-circle'} style={busy === 'ajout' ? { animation: 'ats-spin .7s linear infinite' } : undefined} /> Ne pas contacter
          </button>
          <div style={{ gridColumn: '1 / -1', fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.45 }}>
            Un profil d'une campagne de sourcing s'ajoute depuis la ligne du profil (« Liste noire ») — même sans fiche, elle est retrouvée ou créée, sans le positionner.
          </div>
        </form>
      )}

      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '14px' }}>
        <input className="erp-field" value={q} onChange={ev => setQ(ev.target.value)} placeholder="Chercher un nom, un email, un téléphone, un motif…" aria-label="Rechercher dans la liste noire" style={{ minWidth: '260px', flex: '1 1 260px', maxWidth: '420px' }} />
        <button type="button" style={chip(!origine)} onClick={() => setOrigine('')}>Toutes</button>
        {origines.map(o => (
          <button key={o.cle} type="button" style={chip(origine === o.cle)} onClick={() => setOrigine(origine === o.cle ? '' : o.cle)} title={o.libelle}>
            <BI icon={ICONE_ORIGINE[o.cle] || 'dot'} style={{ fontSize: '10px' }} /> {o.libelle.replace(/^(depuis |à sa demande, |par )/, '')}{c[o.cle] ? <span style={{ ...mono, fontWeight: 400, opacity: .8 }}> {c[o.cle]}</span> : null}
          </button>
        ))}
      </div>

      {erreur === 'arrive' && <div className="erp-card" style={{ padding: '14px 16px', fontSize: '13px', color: 'var(--text-2)' }}>La liste noire arrive côté serveur — la marque « ne pas contacter » reste posable et lisible sur chaque fiche.</div>}
      {erreur && erreur !== 'arrive' && <div className="erp-card" style={{ padding: '14px 16px', fontSize: '13px', color: 'var(--danger-fg)', border: '1px solid var(--danger-fg)' }}>{erreur}</div>}
      {data && entrees.length === 0 && !erreur && (
        <div className="erp-card" style={{ padding: '18px 16px', fontSize: '13px', color: 'var(--text-2)', lineHeight: 1.55 }}>
          {q || origine ? 'Personne ne correspond à cette recherche.' : <>Personne n'est marqué « ne pas contacter » pour l'instant. On y met quelqu'un depuis sa fiche, depuis la ligne d'un profil dans une campagne de sourcing, ou ici avec <b>Ajouter une personne</b>.</>}
        </div>
      )}

      {entrees.length > 0 && (
        <div style={{ border: '1px solid var(--border)', borderRadius: '11px', background: 'var(--surface)', overflow: 'hidden' }}>
          {entrees.map((e, i) => {
            const cle = e.candidate_id != null ? `c${e.candidate_id}` : `v${e.conversation_id}`;
            const enCours = busy === e.candidate_id || busy === `conv-${e.conversation_id}`;
            const h = e.candidate_id != null ? histo[e.candidate_id] : null;
            return (
              <div key={cle} style={{ borderTop: i ? '1px solid var(--hair)' : 'none', boxShadow: e.sans_fiche ? 'inset 3px 0 0 var(--brand)' : 'none' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '64px minmax(180px, 1.1fr) minmax(200px, 2fr) auto', gap: '12px', alignItems: 'start', padding: '11px 14px' }}>
                  <span style={{ ...mono, fontSize: '11.5px', color: 'var(--text-3)', paddingTop: '2px' }} title={e.depuis ? `Depuis le ${fmtDate(e.depuis)}` : 'Date inconnue'}>{fmtDate(e.depuis) || '—'}</span>
                  <div style={{ minWidth: 0 }}>
                    {e.candidate_id != null && typeof onOuvrirCandidat === 'function'
                      ? <button type="button" onClick={() => onOuvrirCandidat(e.candidate_id)} title="Ouvrir la fiche" style={{ border: 'none', background: 'none', padding: 0, font: 'inherit', fontWeight: 700, fontSize: '13.5px', color: 'var(--text-strong)', cursor: 'pointer', textAlign: 'left' }}>{e.nom || `Fiche ${e.candidate_id}`}</button>
                      : <span style={{ fontWeight: 700, fontSize: '13.5px', color: 'var(--text-strong)' }}>{e.nom || 'Sans nom'}</span>}
                    <div style={{ ...mono, fontSize: '11px', color: 'var(--text-3)', marginTop: '2px', display: 'flex', flexWrap: 'wrap', gap: '4px 10px' }}>
                      {e.email && <span>{e.email}</span>}
                      {e.telephone && <span>{e.telephone}</span>}
                      {e.linkedin_url && <a href={e.linkedin_url} target="_blank" rel="noreferrer" style={{ color: 'inherit' }} onClick={ev => ev.stopPropagation()}><BI icon="linkedin" style={{ fontSize: '10px' }} /> profil</a>}
                      {!e.email && !e.telephone && !e.linkedin_url && e.recruiter_id && <span title="Clé de la page Recruiter : c'est par elle que la barrière reconnaît ce profil">clé LinkedIn</span>}
                    </div>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '13px', color: 'var(--text-strong)', lineHeight: 1.45 }}>{e.motif || <span style={{ color: 'var(--text-3)' }}>Sans motif — le prochain recruteur ne saura pas pourquoi.</span>}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '5px' }}>
                      <span style={chipOrigine} title={e.origine_libelle}><BI icon={ICONE_ORIGINE[e.origine] || 'dot'} style={{ fontSize: '9px' }} /> {e.origine_libelle}</span>
                      {e.par_nom && <span style={{ fontSize: '11.5px', color: 'var(--text-3)' }}>par {e.par_nom}</span>}
                      {e.sans_fiche && <span style={{ fontSize: '11.5px', color: 'var(--danger-fg)', fontWeight: 600 }}>Sans fiche : la barrière ne tient que par sa clé de conversation.</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {e.sans_fiche
                      ? <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" disabled={enCours} onClick={() => creerFiche(e)} title="Créer sa fiche, marquée « ne pas contacter » : la barrière la reconnaîtra partout (page, email, téléphone), campagnes suivantes comprises. Elle n'est positionnée sur aucun besoin.">
                          <BI icon={enCours ? 'arrow-repeat' : 'person-plus'} style={enCours ? { animation: 'ats-spin .7s linear infinite' } : undefined} /> Créer sa fiche
                        </button>
                      : <>
                          <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => voirHistorique(e)} title="Poses et levées de la marque sur cette fiche"><BI icon="clock-history" /> {h ? 'Replier' : 'Historique'}</button>
                          <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" disabled={enCours} onClick={() => lever(e)} title="Lever la marque : les envois vers cette personne redeviennent possibles">
                            <BI icon={enCours ? 'arrow-repeat' : 'unlock'} style={enCours ? { animation: 'ats-spin .7s linear infinite' } : undefined} /> Autoriser
                          </button>
                        </>}
                  </div>
                </div>
                {h && (
                  <div style={{ padding: '0 14px 12px 90px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {h.length === 0 && <span style={{ fontSize: '12px', color: 'var(--text-3)' }}>Aucune trace : la marque date d'avant l'historique.</span>}
                    {h.map((x, k) => (
                      <div key={k} style={{ display: 'flex', gap: '10px', alignItems: 'baseline', fontSize: '12px', color: 'var(--text-2)' }}>
                        <span style={{ ...mono, fontSize: '11px', color: 'var(--text-3)', minWidth: '64px' }}>{fmtDate(x.le)}</span>
                        <BI icon={x.quoi === 'pose' ? 'slash-circle' : 'unlock'} style={{ fontSize: '10px', color: x.quoi === 'pose' ? 'var(--danger-fg)' : 'var(--text-3)' }} />
                        <span>{x.quoi === 'pose' ? 'Marqué' : 'Contact autorisé'}{x.motif ? ` — ${x.motif}` : ''}{x.origine_libelle ? ` · ${x.origine_libelle}` : ''}{x.par_nom ? ` · ${x.par_nom}` : ''}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
